/**
 * useSupportTickets — eigene Support-Tickets einsehen und beantworten.
 *
 * GET  /api/v2/support/tickets
 * GET  /api/v2/support/tickets/:id
 * POST /api/v2/support/tickets/:id/reply
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

export type SupportTicketMessage = {
  id: number
  sender: 'user' | 'admin'
  body: string
  created_at: string
}

export type SupportTicket = {
  id: number
  subject: string
  status: 'offen' | 'geschlossen'
  created_at: string
  updated_at: string
}

export type SupportTicketDetail = SupportTicket & {
  messages: SupportTicketMessage[]
}

async function fetchSupportTickets(): Promise<SupportTicket[]> {
  // Cast: apiClient.GET('/api/v2/support/tickets') resolves to `never` — ein
  // vorbestehender TS/openapi-fetch-Overload-Bug bei bestimmten parameterlosen
  // GET-Pfaden (betrifft auch /api/v2/support/donation, nicht durch dieses
  // Feature verursacht). Laufzeitverhalten ist unveraendert korrekt.
  const result = (await apiClient.GET('/api/v2/support/tickets')) as {
    data?: { data?: SupportTicket[] }
    error?: unknown
  }
  if (result.error) throw result.error
  return result.data?.data ?? []
}

export function useSupportTickets() {
  const query = useQuery({
    queryKey: ['support-tickets'],
    queryFn: fetchSupportTickets,
  })
  return { tickets: query.data, isLoading: query.isLoading, isError: query.isError, refetch: query.refetch }
}

export function useSupportTicket(id: number | undefined) {
  const query = useQuery({
    queryKey: ['support-tickets', id],
    queryFn: async () => {
      const result = await apiClient.GET('/api/v2/support/tickets/{id}', { params: { path: { id: id! } } })
      if (result.error) throw result.error
      return result.data as SupportTicketDetail
    },
    enabled: id != null,
  })
  return { ticket: query.data, isLoading: query.isLoading, isError: query.isError, refetch: query.refetch }
}

export function useReplySupportTicket(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (message: string) => {
      const result = await apiClient.POST('/api/v2/support/tickets/{id}/reply', {
        params: { path: { id } },
        body: { message },
      })
      if (result.error) throw result.error
      return result.data as SupportTicketDetail
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['support-tickets', id], data)
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
    },
  })
}
